// Pure design tokens for HealthDesk. No imports — single source of truth for apps and shared packages.

const tokens = {
  colors: {
    primary: { light: "#1B4584" },
    secondary: { light: "#117ACA" },
    link: { light: "#117ACA" },
    bgPage: { light: "#F5F8FC" },
    /** Symptom checker consent modal initials panel */
    consentHighlightPanel: { light: "#f8fafc" },
    bgCard: { light: "#FFFFFF" },
    bgSurface: { light: "#FFFFFF" },
    textPrimary: { light: "#333333" },
    /** Chatscope incoming / editor text (matches legacy kit) */
    legacyChatMessageText: { light: "#212121" },
    textSecondary: { light: "#666666" },
    textDisabled: { light: "#9E9E9E" },
    msiBlue1: { light: "#2a5a9f" },
    msiBlue2: { light: "#1B4584" },
    /** Satin AppBar / sponsor SVG gradient mid stops */
    msiBlueSatinMid: { light: "#163a6f" },
    msiBlueSatinHighlight: { light: "#235394" },
    msiBlueSatinDeep: { light: "#0f2c57" },
    border: { light: "#E0E0E0" },
    borderStrong: { light: "#BDBDBD" },
    /** Outlined primary control hover (Sign In, map Explore) */
    primaryOutlinedHover: { light: "#153a6d" },
    /** ChartMind outlined control hover (matches legacy #153a6e) */
    chartMindOutlinedHover: { light: "#153a6e" },
    /** CareMap header outlined button hover border */
    mapHeaderOutlinedHoverBorder: { light: "#163a6b" },
    /** MSI brand accent (matches --msi-red on splash) */
    msiRed: { light: "#841B45" },
    /** MSI brand green (matches --msi-green on splash) */
    msiGreen: { light: "#45841B" },
    /** Declined-queue UI hover border */
    msiRedHoverBorder: { light: "#6a1537" },
    /** Red CTA gradient end (MSI³ splash primary button) */
    msiRedGradientEnd: { light: "#6a1638" },
    /** Light tint behind declined / MSI alerts */
    msiRedTint: { light: "#fdf0f3" },
    /** Upper stop on global-manager MSI gradient tiles */
    msiBlueUpper: { light: "#2458A6" },
    /** iOS-style list / input hairlines */
    iosSeparator: { light: "#c8c7cc" },
    /** Public chat header button hover (exact legacy value) */
    secondaryDeepPress: { light: "#0e63a2" },
    /** Neutral grays (legacy chat / helpers) */
    neutralLight: { light: "#eeeeee" },
    neutralMid: { light: "#999999" },
    /** Pure black for split-flap wells, strokes */
    uiBlack: { light: "#000000" },
    /** Settings / mic accent */
    uiPurple: { light: "#9c27b0" },
    /** Health hub category accents (CMR hub cards) */
    hubMedications: { light: "#2e7d32" },
    hubAllergies: { light: "#ed6c02" },
    hubOtherMuted: { light: "#616161" },
    /** Soft warning panel background (ChartMind, MUI-adjacent) */
    warningMutedBg: { light: "#fff3e0" },
    /** Info / primary-tint list selection (patient symptom DDX) */
    infoMutedBg: { light: "#e3f2fd" },
    /** Amber-tint list selection (patient DDX custom additions) */
    amberSelectionMuted: { light: "#fff8e1" },
    /** Default node color for DDX graph fallback */
    chartMindNodeDefault: { light: "#1976d2" },
    /** CareMap / context density visualization */
    densityMapDefault: { light: "#6C7073" },
    densityMapMid: { light: "#1B4685" },
    densityMapHigh: { light: "#127BCB" },
    densityMapHighlight: { light: "#1C4685" },
    /** Global manager split-flap board (DashboardNav) */
    navGlobalManagerBoard: { light: "#0f1a2d" },
    navGlobalManagerGradientStart: { light: "#111823" },
    navGlobalManagerGradientEnd: { light: "#0d131c" },
  },
  clinical: {
    critical: { light: "#C62828" },
    high: { light: "#E65100" },
    medium: { light: "#F9A825" },
    low: { light: "#1565C0" },
  },
  /** GitHub-style dark UI used by split-flap tooling and title logo pages */
  splash: {
    github: {
      gradientStart: "#0f1419",
      gradientEnd: "#1a2332",
      text: "#e6edf3",
      muted: "#8b949e",
      border: "#30363d",
      canvas: "#0d1117",
      surfaceRaised: "#21262d",
      surfaceModal: "#161b22",
      accent: "#58a6ff",
      accentHover: "#79b8ff",
      titleLogosGreen: "#238636",
      titleLogosGreenHover: "#2ea043",
      btnSecondary: "#30363d",
      btnSecondaryHover: "#484f58",
    },
    /** MSI³ splash page (radial bg, tiles, copy) */
    msi3: {
      pageBg: "#0a1220",
      panel: "#102847",
      panel2: "#163661",
      text: "#f5f8ff",
      muted: "#a9bad8",
      hinge: "#081a31",
      hingeHighlight: "#335f98",
      radialStart: "#133058",
      radialMid: "#0b1830",
      radialEnd: "#060d19",
      tileWell: "#000000",
      descriptionStrong: "#c8d6ef",
    },
    /** MSI splash alt page (MSISplashAltPage inline CSS — exact radial + copy tints) */
    msiAlt: {
      radial1: "#163868",
      radial2: "#102848",
      radial3: "#0b1830",
      radial4: "#07101f",
      textOnGradient: "#ffffff",
      subtitleSoft: "#d9e7ff",
      displayTitle: "#dbe8ff",
      descriptor: "#d8e5fb",
    },
    /** Advertiser promotional split-flap (gold + satin variants) */
    promotionalFlap: {
      boardGold: "#2a2210",
      lowerGold: "#b78928",
      upperGold: "#f5dc8b",
      frameGradGoldStart: "#3a3020",
      frameGradGoldEnd: "#1a1410",
    },
    /** Gold brushed AppBar SVG (`GoldBrushedBarBackgroundSvg`) */
    goldBrushedBar: {
      stop0: "#fff6c9",
      stop20: "#f0d57a",
      stop40: "#cda43a",
      stop60: "#f5dc8b",
      stop80: "#b78928",
      stop100: "#7a5617",
    },
  },
  /** Patient purple satin AppBar SVG gradient stops */
  purpleSatinGradient: {
    stop0: "#9a46bf",
    stop18: "#7B1FA2",
    stop40: "#65178a",
    stop60: "#8a32b1",
    stop82: "#7B1FA2",
    stop100: "#4f1368",
  },
  /** Split-flap title bars / logo page tile colors */
  splitFlap: {
    chartmindUpperFlap: "#1F8DE2",
    masterchartUpperFlap: "#9A33C8",
    msiTitleLower: "#0B1626",
    msiTitleUpper: "#13263F",
    /** QueueDisplay hinge end-cap metallic cylinder */
    hingeCapMetallicGradient:
      "linear-gradient(90deg, #1f2329 0%, #4b5159 22%, #d3d8de 48%, #a2a8b0 52%, #5b626a 78%, #1b1f25 100%)",
  },
  /** SplitFlapDisplayPage.jsx dev defaults (tooling) */
  splitFlapDisplayPage: {
    frame: "#2a2a2a",
    upperFlap: "#1a4d6e",
    lowerFlap: "#0d2d44",
  },
  mapMarker: {
    score0: "#C31111",
    score33: "#F59802",
    score66: "#5BA218",
    score100: "#118B1F",
    /** CareMap overlay layer marker when config/meta omit color */
    layerCategoryFallback: "#C0392B",
  },
  /** Legacy in-app chat (Chatscope-adjacent) — preserve exact hex */
  chatLegacy: {
    editorBg: "#f6f7f8",
    chrome: "#686e72",
    bubbleRed: "#ff7979",
    bubbleYellow: "#ffe14a",
    inputSurface: "#f9f9f9",
    inputSurfaceAlt: "#f5f5f5",
    scrollAccent: "#1976d2",
    actionBlue: "#1565c0",
    actionBluePressed: "#0d47a1",
  },
  symptomCheck: {
    voiceAccent: "#7B1FA2",
    voiceAccentHover: "#6A1B9A",
  },
  chartMindIos: {
    errorMutedBg: "#ffebee",
    successMutedBg: "#e8f5e9",
  },
  /** Clinical reference PDF tag categories (LLM manager panel) */
  llmReferenceTag: {
    symptoms: "#e91e63",
    diagnoses: "#2196f3",
    tests: "#ff9800",
    treatments: "#4caf50",
    general: "#607d8b",
  },
  typography: {
    fontSizes: [12, 14, 16, 20, 24, 32],
    size: {
      caption: 12,
      dense: 14,
      body: 16,
      subhead: 20,
      heading: 24,
      display: 32,
    },
    fontFamilyBody: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontFamilyHeading: '"Federo", serif',
    fontFamilyMono:
      '"source-code-pro", "Menlo", "Monaco", "Consolas", "Courier New", monospace',
  },
  spacing: {
    touchTarget: 44,
  },
  radius: {
    card: 8,
    chip: 16,
    button: 6,
    dialog: 12,
  },
  layout: {
    drawerWidthOpen: 300,
    drawerWidthClosed: 72,
    appBarHeight: 64,
    appBarHeightMobile: 56,
    contentMaxWidth: 1280,
  },
};

export default tokens;
